import { useState } from "react";

import { trpc } from "@blis/trpc/react";

import { TestSpecLabel } from "@/components/TestSpecLabel";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import type { TInstrument } from "@/lib/types";
import { useDialogProps } from "../detail/state";

export function InstrumentStageDialog({
  accession,
}: {
  accession: { id: number };
}) {
  const dialogProps = useDialogProps("instrument");

  const [selected, setSelected] = useState<TInstrument | null>(null);

  const instruments = trpc.instruments.list.useQuery();
  const orders = trpc.instruments.listTestOrders.useQuery(
    {
      accessionId: accession.id,
      instrumentId: selected ? selected.id : 0,
    },
    {
      enabled: !!selected,
    },
  );

  const stage = trpc.instruments.stageAccession.useMutation({
    onSuccess: () => {
      dialogProps.onOpenChange?.(false);
      toast.success("Accession staged for instrument", {
        description: `Tests staged for: ${selected?.manufacturer} ${selected?.model}`,
      });
    },
    onError: (err) => {
      toast.error("Error staging accession", {
        description: err.message,
      });
    },
  });

  const submit = () => {
    if (selected) {
      stage.mutate({
        accessionId: accession.id,
        instrumentId: selected.id,
      });
    }
  };

  return (
    <Modal isOpen={dialogProps.open} onOpenChange={dialogProps.onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Instrument Staging</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text muted>
            This stages (or re-stages) tests from the accession to be pulled
            from the provided instrument.
          </Text>
          <Select
            id="instrument-stage-select"
            name="instrument-select"
            placeholder="Select instrument"
            selectedKey={selected?.id ?? null}
            onSelectionChange={(key) => {
              const instrument = instruments.data?.find((i) => i.id === key);
              if (instrument) {
                setSelected(instrument);
              }
            }}
          >
            <Label>Instrument</Label>
            <SelectTrigger />
            <SelectList items={instruments.data || []}>
              {(item) => (
                <SelectItem id={item.id} textValue={item.model}>
                  {item.manufacturer} {item.model}
                </SelectItem>
              )}
            </SelectList>
          </Select>
          {orders.data && orders.data.length > 0 ? (
            <div className="pt-3">
              <Text className="pb-3">
                The following tests will be staged for {selected?.manufacturer}{" "}
                {selected?.model}:
              </Text>
              <ul className="list-none space-y-1.5 ps-0">
                {orders.data.map((to) => (
                  <li key={to.id}>
                    <TestSpecLabel
                      name={to.testSpec.name}
                      code={to.testSpec.code}
                      className="text-sm"
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : orders.data && orders.data.length === 0 ? (
            <Text size="2" muted className="pt-3">
              No tests found for instrument: {selected?.manufacturer}{" "}
              {selected?.model}
            </Text>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button
            isDisabled={orders.data?.length === 0}
            isPending={stage.isPending}
            onPress={submit}
          >
            Submit
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
